<template>
  <div class="sms-template-list">
    <vs-tabs
      position="top"
      v-model="tabActive"
      class="tabs-shadow-none">

      <vs-tab
        v-for="(tab, index) in tabs"
        :key="index"
        icon-pack="feather"
        :icon="tab.icon"
        :label="tab.label"
        class="p-0"
      >
      </vs-tab>
    </vs-tabs>

    <vs-divider/>

    <template v-if="tabActive === 0">
      <import-excel-to-json
        :key="importComponentKey"
        v-if="showImportComponent"
        :target-headers="columnDefsToImport"
        :entity="entity"
        :on-save-import="importSMSTemplatesFromFile"
        :current-import-job="activeImportJob"
        @refresh="importComponentKey+=1"
        @imported="resetDataSourceAndSelection()"
        @close="closeImport"/>

      <div
        v-show="!showImportComponent"
        class="vx-card p-6">
        <base-ag-grid-header
          :rows-selected-count="rowsSelectedCountWithAllSelection"
          :filters-count="filtersAppliedCount"
          :filters-match.sync="filtersMatch"
          @reset-selection="setSelectedRowsNone()"
          @reset-filters="resetFilters()">
          <template v-slot:header-left>
            <items-per-page
              :count="count"
              :currentPage="currentPage"
              :paginationPageSize="paginationPageSize"
              @changed-page-size="paginationSetPageSize">
            </items-per-page>

            <list-action-drop-down
              v-if="isAnyRowsSelected"
              :options="allMultipleActionOptions"
              class="ml-3"
              @export="exportData()"
              @delete="confirmDeleteRecords()"
              @restore="confirmRestoreRecordsForTrashWithAgGrid()">
            </list-action-drop-down>
          </template>

          <template v-slot:header-right>
            <page-header-actions
              class="hidden md:flex"
              :actions="headerActions"
              @action="onHeaderAction"
            />
          </template>
        </base-ag-grid-header>

        <selection-alert-info
          v-model="selectionPageAlert"
          class="mb-5"
          :entity="entity"
          :count="count"
          :rows-selected-count="rowsSelectedCount"
          @select-all="setSelectedRowsAll()"></selection-alert-info>

        <export-alert-info
          :entity="$tc(`$Entities.${entity}`, 2)"
          :exporting.sync="exportingList"
          :exported.sync="exportedList"
          :fileUrl="urlFileExported"
          :send-email="exportSendEmail"/>

        <ag-grid-vue
          :key="agGridKey"
          ref="agGridTable"
          :domLayout="domLayout"
          :style="gridStyle"
          :components="components"
          :frameworkComponents="frameworkComponents"
          :gridOptions="gridOptions"
          class="ag-theme-material w-100 ag-grid-table"
          :columnDefs="columnDefs"
          :defaultColDef="defaultColDef"
          :column-types="columnTypes"
          :getRowNodeId="getRowNodeId"
          :autoParamsRefresh="true"
          rowSelection="multiple"
          :animateRows="true"
          :suppressRowClickSelection="true"
          rowModelType="infinite"
          :pagination="hasPagination"
          :paginationPageSize="paginationPageSize"
          :suppressPaginationPanel="suppressPaginationPanel"
          :overlayLoadingTemplate="overlayLoadingTemplate"
          :noRowsOverlayComponent="noRowsOverlayComponent"
          :noRowsOverlayComponentParams="noRowsOverlayComponentParams"
          :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
          :cacheBlockSize="cacheBlockSize"
          :maxBlocksInCache="maxBlocksInCache"
          @selection-changed="onSelectionChanged"
          @grid-ready="onGridReady"
          @model-updated="onModelUpdate"
          @grid-size-changed="onGridSizeChanged"
          @first-data-rendered="onFirstDataRendered"
          @row-clicked="onRowClicked"
          @row-double-clicked="onRowDoubleClicked"
          @filter-changed="onFilterChanged"
          @pagination-changed="onPaginationChanged">
        </ag-grid-vue>

        <!-- Mobile Sticky Bottom Bar -->
        <div
          class="sticky-bottom-bar-icons md:hidden"
          :style="{ gridTemplateColumns: `repeat(${(isAnyRowsSelected ? 1 : 0) + (trash ? 1 : 3)}, 1fr)` }"
        >
          <vs-dropdown
            v-if="isAnyRowsSelected"
            vs-custom-content
            vs-trigger-click
            class="mobile-bar-btn mobile-bar-btn-simple"
          >
            <div class="mobile-bar-btn-content">
              <i class="feather icon-more-horizontal mobile-bar-icon"></i>
              <span class="mobile-btn-label">Actions</span>
            </div>
            <vs-dropdown-menu class="mobile-actions-dropdown gp-sticky-dropdown-menu">
              <ul class="mobile-actions-list">
                <li
                  v-for="(opt, idx) in mobileStickyBulkActionOptions"
                  :key="`mba-${idx}`"
                  class="mobile-actions-item"
                  :class="{ disabled: !!opt.disabled }"
                  @click="opt.disabled ? null : onMobileBulkAction(opt.event)"
                >
                  <span class="flex items-center">
                    <feather-icon :icon="opt.icon" svgClasses="h-4 w-4" class="mr-2" />
                    <span>{{ opt.label }}</span>
                  </span>
                </li>
              </ul>
            </vs-dropdown-menu>
          </vs-dropdown>

          <button
            v-if="!trash"
            class="mobile-bar-btn mobile-bar-btn-simple"
            type="button"
            @click="newItem()"
          >
            <i class="feather icon-plus mobile-bar-icon"></i>
            <span class="mobile-btn-label">Add</span>
          </button>

          <button
            v-if="!trash"
            class="mobile-bar-btn mobile-bar-btn-simple"
            type="button"
            @click="showImportComponent=true"
          >
            <i class="feather icon-upload mobile-bar-icon"></i>
            <span class="mobile-btn-label">Import</span>
          </button>

          <button
            class="mobile-bar-btn mobile-bar-btn-simple mobile-bar-btn-refresh"
            type="button"
            @click="resetDataSourceAndSelection()"
          >
            <i class="feather icon-refresh-cw mobile-bar-icon"></i>
            <span class="mobile-btn-label">Refresh</span>
          </button>
        </div>

        <vs-popup
          :title="titleModal"
          :active.sync="activeModalCreateOrEdit">
          <transition name="zoom-fade">
            <s-m-s-template-list-create-or-edit
              v-if="showCreateOrEditComponent"
              ref="createOrEdit"
              :entity="entity"
              :operation="operation"
              :model-payload="recordSelected"
              :actions="actionsOnCreateOrEdit"
              @action="onActionFromCreateOrEditForTemplate"
              @model-was-changed="(val) => this.createOrEditModelWasChanged=val"
              @saved="savedItemOnAgGridTable"
              @close="activeModalCreateOrEdit=false">
            </s-m-s-template-list-create-or-edit>
          </transition>
        </vs-popup>

        <vs-popup
          :title="exportModalTitle"
          :active.sync="activeModalToExport">
          <transition name="zoom-fade">
            <export-json-to-excel
              v-if="showExportComponent"
              :columns="getColumnsToExport"
              :data="rowsSelected"
              @close="activeModalToExport=false"
              @export="onExport">
            </export-json-to-excel>
          </transition>
        </vs-popup>
      </div>
    </template>

    <template v-else>
      <div class="vx-card p-6">
        <waiting-list-configuration-template
          ref="configurationTemplate"
          :tab-index.sync="configTemplatesTabIndex"
          :show-update="true"
        />
      </div>
    </template>
  </div>
</template>

<script>
import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss';
import { mapActions, mapMutations } from 'vuex';

// Mixins
import commonTemplateListCreateOrEdit from '@/views/modules/templates/mixins/commonTemplateListCreateOrEdit';

// Cell Renderer
import CellRendererActions from '@/views/modules/templates/TemplateCellRendererActions.vue';
import CellRendererLargeText from '@/views/modules/_components/cell-renderer/CellRendererLargeTextModal.vue';

// Custom Components
import ImportExcelToJson from '@/views/modules/_components/import-exce-to-json/ImportExcelToJson.vue';
import BaseAgGridHeader from '@/views/modules/_components/BaseAgGridHeader.vue';
import ItemsPerPage from '@/views/modules/_components/ItemsPerPage.vue';
import ExportJsonToExcel from '@/views/modules/_components/ExportJsonToExcel.vue';
import ListActionDropDown from '@/views/modules/_components/ListActionDropDown.vue';
import SMSTemplateListCreateOrEdit from '@/views/modules/templates/sms-template/SMSTemplateListCreateOrEdit.vue';
import AgGridSelectionHeader from '@/views/modules/_components/AgGridSelectionHeader';
import ExportAlertInfo from '@/views/modules/_components/ExportAlertInfo.vue';
import CustomNoRowsOverlay from '@/views/modules/_components/customAgGridNoRowsOverlay';
import SelectionAlertInfo from '@/views/modules/SelectionAlertInfo.vue';
import WaitingListConfigurationTemplate from '@/views/modules/waiting-list/WaitingListConfigurationTemplates.vue';
import smsTemplateService from '@/api/modules/sms-template.service';
import PageHeaderActions from '@/views/modules/_components/PageHeaderActions.vue';

/**
 * Component to list and manage sms templates
 *
 * @module views/modules/templates/SMSTemplateList
 * @author Dilan Useche <dilan8810@gmail.com>
 */
export default {
  name: 'SMSTemplateList',
  i18n: {
    messages: {
      en: {
        ThereAreNoSmSTemplates: 'No SMS templates to display',
        $NoRowsOverlay: {
          Title: 'No SMS templates to display',
          Text: 'There is nothing here to view right now. Create a new SMS template to get started.',
          Button: 'Create new SMS template',
        },
        $NoRowsOverlayBin: {
          Title: 'No SMS templates in the bin',
          Text: 'The bin is empty. There are no deleted SMS templates to display.',
          Button: 'Create new SMS template',
        },
        $Tabs: {
          Campaigns: 'Campaigns',
          WaitingList: 'Waitlist',
        },
      },
    },
  },
  components: {
    BaseAgGridHeader,
    ExportJsonToExcel,
    ExportAlertInfo,
    ImportExcelToJson,
    ItemsPerPage,
    ListActionDropDown,
    SMSTemplateListCreateOrEdit,
    PageHeaderActions,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererActions,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererLargeText,
    SelectionAlertInfo,
    WaitingListConfigurationTemplate,
  },
  mixins: [commonTemplateListCreateOrEdit],
  data() {
    return {
      entity: this.$enums.Entity.SMS_TEMPLATE,
      exportCollectionFunction: (data) => this.exportSMSTemplates({
        exportInfo: data,
        trash: this.trash,
      }),
      fetchAllCollectionFunction: this.fetchAllSMSTemplates,
      fetchAllCollectionTrashFunction: this.fetchAllSMSTemplatesFromTrash,
      deleteRecordFunction: (id) => this.removeSMSTemplate({
        smsTemplateId: id,
        trash: this.trash,
      }),
      deleteRecordsFunction: (data) => this.removeSMSTemplates({ ...data, trash: this.trash }),
      restoreItemFunction: this.restoreSMSTemplate,
      restoreItemsFunction: this.restoreSMSTemplates,
      sendCampaignFromTemplateFunction: this.sendSMSCampaignFromTemplate,
      preferFitColumns: true,
      noRowsMessage: this.$t('ThereAreNoSmSTemplates'),

      activeImportJob: null,

      tabActive: 0,
      tabs: {
        campaigns: {
          icon: 'icon-message-circle',
          iconPack: 'feather',
          label: this.$t('$Tabs.Campaigns'),
        },
        waitingList: {
          icon: 'icon-users',
          iconPack: 'feather',
          label: this.$t('$Tabs.WaitingList'),
        },
      },
      configTemplatesTabIndex: 0,

      columnDefs: [
        {
          colId: 'name',
          headerName: this.$t('$General.Name'),
          field: 'name',
          filter: 'agTextColumnFilter',
          minWidth: 250,
          maxWidth: 450,
          checkboxSelection: true,
          headerComponentParams: { checkboxSelection: true },
          requireOnImport: true,
        },
        {
          colId: 'message',
          headerName: this.$tc('$General.Message'),
          field: 'message',
          filter: 'agTextColumnFilter',
          minWidth: 250,
          maxWidth: 1906,
          requireOnImport: true,
          cellRendererFramework: 'CellRendererLargeText',
        },
      ],
      components: {
        CellRendererActions,
        CellRendererLargeText,
      },
      frameworkComponents: {
        agColumnHeader: AgGridSelectionHeader,
        customNoRowsOverlay: CustomNoRowsOverlay,
      },
    };
  },
  computed: {
    mobileStickyBulkActionOptions() {
      const options = [...this.allMultipleActionOptions];
      const canEdit = !this.editDisabled && !this.trash;

      if (canEdit) {
        options.unshift({
          label: this.$t('$General.Edit'),
          event: this.$enums.Operation.EDIT,
          icon: 'Edit2Icon',
          disabled: this.selectedAllRows || this.rowsSelectedCount !== 1,
        });
      }

      return options;
    },
    headerActions() {
      const actions = [];

      if (!this.trash) {
        actions.push({
          id: 'add',
          tooltip: this.$t('$General.AddNew'),
          kind: 'icon',
          icon: 'icon-plus',
        });
        actions.push({
          id: 'import',
          tooltip: this.$t('$General.Import'),
          kind: 'icon',
          icon: 'icon-upload',
        });
      }

      actions.push({
        id: 'refresh',
        tooltip: 'Refresh',
        kind: 'icon',
        icon: 'icon-refresh-cw',
      });

      return actions;
    },
  },
  beforeMount() {
    const $this = this;
    this.noRowsOverlayComponent = 'customNoRowsOverlay';

    // Use different messages for bin/trash mode
    const overlayKey = this.trash ? '$NoRowsOverlayBin' : '$NoRowsOverlay';

    this.noRowsOverlayComponentParams = {
      title: this.$t(`${overlayKey}.Title`),
      text: this.$t(`${overlayKey}.Text`),
      buttonText: this.trash ? null : this.$t(`${overlayKey}.Button`),
      async actionButtonClicked() {
        if (!$this.trash) {
          await $this.newItem();
        }
      },
    };
  },
  async created() {
    this.$vs.loading({ type: 'radius' });

    const resp = await smsTemplateService.getActiveImport();
    this.activeImportJob = resp.data;

    this.$vs.loading.close();
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler(newVal) {
        if (newVal.importReport) {
          this.showImportComponent = true;
        }
      },
    },
  },
  methods: {
    onMobileBulkAction(event) {
      switch (event) {
        case this.$enums.Operation.EDIT:
          if (this.rowsSelectedCount === 1 && this.rowsSelected && this.rowsSelected[0]) {
            this.editRecord(this.rowsSelected[0]);
          }
          break;
        case 'export':
          this.exportData();
          break;
        case 'delete':
          this.confirmDeleteRecords();
          break;
        case 'restore':
          this.confirmRestoreRecordsForTrashWithAgGrid();
          break;
        default:
          break;
      }
    },
    onHeaderAction({ id }) {
      switch (id) {
        case 'add':
          this.newItem();
          break;
        case 'import':
          this.showImportComponent = true;
          break;
        case 'refresh':
          this.resetDataSourceAndSelection();
          break;
        default:
          break;
      }
    },
    ...mapActions({
      fetchAllSMSTemplates: 'smsTemplate/fetchAllSMSTemplates',
      fetchAllSMSTemplatesFromTrash: 'smsTemplate/fetchAllSMSTemplatesFromTrash',
      importSMSTemplatesFromFile: 'smsTemplate/importFromFile',
      exportSMSTemplates: 'smsTemplate/exportFile',
      removeSMSTemplate: 'smsTemplate/removeSMSTemplate',
      removeSMSTemplates: 'smsTemplate/removeSMSTemplates',
      restoreSMSTemplate: 'smsTemplate/restoreSMSTemplate',
      restoreSMSTemplates: 'smsTemplate/restoreSMSTemplates',
    }),
    ...mapMutations({
      setTemplateToCampaign: 'smsCampaign/SET_FROM_SMS_TEMPLATE',
    }),
    async fetchAgGridData(params) {
      const filters = {
        agGridFilters: {
          filterType: this.$enums.AppFilterType.CONDITIONAL,
          filtersMatch: params.filtersMatch,
          filter: params.filters,
        },
        waitingListMessage: {
          filterType: this.$enums.AppFilterType.TEXT,
          type: this.$enums.AppFilter.FilterType.Text.Type.EQUALS,
          filter: '',
          allowEmpty: true,
        },
        importing: {
          filterType: this.$enums.AppFilterType.BOOLEAN,
          filter: this.$enums.AppFilter.FilterType.Boolean.Values.NO,
        },
      };
      const filtersMatch = this.$enums.AppFilterMathType.ALL;

      return this.fetchCollection({
        ...params,
        filtersMatch,
        filters,
      });
    },
    sendSMSCampaignFromTemplate(smsTemplate) {
      this.setTemplateToCampaign(smsTemplate);
      this.$router.push({ name: 'sms-campaigns-create' });
    },
    closeImport() {
      this.showImportComponent = false;
      this.resetDataSourceAndSelection();
    },
  },
};
</script>

<style scoped lang="scss">
// Mobile Sticky Bottom Bar (icon buttons)
.sticky-bottom-bar-icons {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 2px solid #E5E7EB;
  padding: 8px 12px;
  display: grid;
  gap: 8px;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 100;
  margin: 0 -1.5rem -1.5rem; // match vx-card p-6

  @media (max-width: 767px) {
    position: -webkit-sticky;
    position: sticky;
  }
}

.mobile-bar-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.15s ease;
  color: #6B7280;
  font-family: inherit;
  outline: none;

  &:active {
    transform: scale(0.95);
    background: rgba(139, 92, 246, 0.1);
  }
}

.mobile-bar-btn-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.mobile-bar-icon {
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 6px auto;
  width: 22px;
  height: 22px;
}

.mobile-btn-label {
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  line-height: 1;
}

.mobile-bar-btn-simple {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }
}

.mobile-bar-btn-refresh {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }

  &:not(:active):not(:hover) {
    background: transparent !important;
  }
}

.mobile-actions-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.mobile-actions-item {
  padding: 10px 12px;
  cursor: pointer;

  &:hover {
    background: rgba(139, 92, 246, 0.06);
  }
}
</style>
